import { Card, CardHeader, CardContent, Typography, Button, CardActions, OutlinedInput, InputAdornment, LinearProgress, FormControl, FormHelperText, Grid } from "@mui/material";
import { Computer, Cloud, Key, VerifiedUser } from '@mui/icons-material';
import { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";

import Helper from "../providers/Helpers";
import commandsProvider from "../providers/CommandsProvider";
import { useConfirmationDialog } from "../providers/ConfirmationDialogProvider";
import { usePromptDialog } from "../providers/PromptDialogProvider";
import { useSnackbar } from "../providers/SnackbarProvider";
import User from "../models/User";

const useStyles = makeStyles({
    card: {
        padding: "0.5rem",
        marginTop: "1rem",
        marginBottom: "1rem",
        position: 'relative',
        overflow: 'hidden'
    },
    key: {
        width: "20rem",
        marginBottom: "0rem"
    }
});

const LICENSE_TYPES = {
    "64": { type: "desktop", label: "PRO Local", icon: <Computer color="primary" /> },
    "72": { type: "web", label: "Web", icon: <Cloud color="primary" /> }
};

const LICENSE_TYPE_MAP = {
    d: { type: "desktop", label: "PRO Local", icon: <Computer color="primary" /> },
    w: { type: "web", label: "Web", icon: <Cloud color="primary" /> },
    f: { type: "free", label: "Free", icon: <VerifiedUser color="success" /> }
};

export default function UserLicenseAction(props) {
    const { openConfirmationDialog } = useConfirmationDialog();
    const { openPromptDialog } = usePromptDialog();
    const { openSnackbar } = useSnackbar();

    const classes = useStyles();
    const [key, setKey] = useState("");
    const [licenseType, setLicenseType] = useState(null);
    const [users, setUsers] = useState([]);
    const [isValid, setIsValid] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isNeedRestart, setIsNeedRestart] = useState(false);
    const [session, setSession] = useState(commandsProvider.getSession());

    // Serial da sessão atual
    const sessionSerial = session?.serial || "";

    // Detecta tipo ao terminar de digitar (ex: 30 caracteres)
    const handleKeyChange = (e) => {
        const value = e.target.value;
        setKey(value);

        // Reset
        setLicenseType(null);
        setIsValid(null);

        // Só tenta validar se tiver tamanho mínimo
        if (value.length >= 10) {
            const decrypted = Helper.LicenseGenerator.decryptKey(value);
            if (decrypted) {
                // O tipo está no início da string descriptografada
                const typeChar = decrypted[0];
                const licenseInfo = LICENSE_TYPE_MAP[typeChar];

                setLicenseType(licenseInfo || null);

                // Validação: tipo e serial
                if (licenseInfo) {
                    // Extrai serial da licença descriptografada
                    const parts = decrypted.split("T");
                    const licenseSerial = parts[1];

                    // Valida tipo e serial
                    const isTypeValid = Helper.LicenseGenerator.validateLicenseType(value, typeChar);
                    const isSerialValid = licenseSerial === sessionSerial;

                    setIsValid(isTypeValid && isSerialValid);
                } else {
                    setIsValid(false);
                }
            } else {
                setIsValid(false);
            }
        }
    };



    // Actions visuais (sem integração)
    const handleActivate = async () => {

        const online = await Helper.TestIfIsOnline();   

        if (!online && licenseType?.type === "web") {
            openSnackbar("Ativação de licença requer conexão com a internet.", "error");
            return;
        }

        openConfirmationDialog({
            title: "Confirmação",
            message: (
                <>
                    Deseja realmente ativar esta licença {licenseType?.type === "desktop" ? "PRO Local" : licenseType?.type.toUpperCase()}<br />
                    ({key})?
                </>
            ),
            confirmButtonText: "Ativar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "primary",
            onConfirm: async () => {
                let needRestart = false;
                let isConflictPending = false;

                const doRestart = async () => {
                    if (needRestart) {
                        if (process.env.REACT_APP_MODE === "desktop" && process.env.REACT_APP_ENVIROMENT === "production") {
                            await fetch(`${process.env.REACT_APP_URL}/account/restart-guardiao`, {
                                method: 'POST',
                                headers: {
                                    "content-type": "application/json",
                                    "Authorization": "Bearer " + props.token
                                }
                            });
                        }
                    }
                    setIsLoading(false);
                };

                try {
                    const session = commandsProvider.getSession();
                    const serial = session.serial;
                    const userId = session.id;
                    let userList = users;


                    if (licenseType?.type === "web" && !VerifyWebMasterUser()) {
                        userList = await ensureWebMasterUser();
                    }

                    if (!userList || userList.length === 0) {
                        setIsLoading(false);
                        return;
                    }

                    setIsLoading(true);

                    const url = `${process.env.REACT_APP_URL}/licences/activate/combined?serialNumber=${serial}&key=${key}&userId=${userId}`;
                    const response = await fetch(url, {
                        method: "POST",
                        headers: { "content-type": "application/json" },
                        body: JSON.stringify(userList)
                    });

                    if (!response.ok) {
                        throw new Error("Erro ao ativar licença");
                    }

                    const resData = await response.json();

                    const messageHandlers = {
                        success: () => {
                            openSnackbar(`Sua licença ${licenseType?.type} foi ativada com sucesso`, "success");
                            setIsNeedRestart(true);
                            needRestart = true;
                            setKey("");
                        },
                        fail: () => openSnackbar(resData.error, "error"),
                        duplicated: () => openSnackbar(resData.error, "error"),
                        emailconflict: () => {
                            isConflictPending = true;
                            openConfirmationDialog({
                                title: "Usuário Master já cadastrado na nuvem",
                                message: (
                                    <>
                                        Já existe um usuário Master cadastrado para este painel.<br /><br />
                                        📧 Master atual na nuvem: <strong>{resData.currentCloudEmail}</strong><br />
                                        📧 Novo Master a ser salvo: <strong>{resData.conflictEmail}</strong><br /><br />
                                        Deseja substituir o usuário Master existente pelo novo?
                                    </>
                                ),
                                confirmButtonText: "Substituir",
                                cancelButtonText: "Manter",
                                confirmButtonColor: "warning",
                                onConfirm: async () => {
                                    try {
                                        const overwriteUrl = `${process.env.REACT_APP_URL}/licences/activate/combined?serialNumber=${serial}&key=${key}&userId=${userId}&forceOverwrite=true`;
                                        const overwriteRes = await fetch(overwriteUrl, {
                                            method: "POST",
                                            headers: { "content-type": "application/json" },
                                            body: JSON.stringify(userList)
                                        });
                                        const overwriteData = await overwriteRes.json();
                                        if (overwriteData.message?.toLowerCase() === "success") {
                                            openSnackbar(`Sua licença ${licenseType?.type} foi ativada com sucesso`, "success");
                                            setIsNeedRestart(true);
                                            needRestart = true;
                                            setKey("");
                                        } else {
                                            openSnackbar(overwriteData.error || "Erro ao substituir permissões.", "error");
                                        }
                                    } catch (e) {
                                        openSnackbar("Erro ao reenviar ativação.", "error");
                                    } finally {
                                        await doRestart();
                                    }
                                },
                                onCancel: async () => {
                                    // Manter: continua a ativação sem trocar nada nos usuários
                                    try {
                                        const skipUrl = `${process.env.REACT_APP_URL}/licences/activate/combined?serialNumber=${serial}&key=${key}&userId=${userId}&skipUsers=true`;
                                        const skipRes = await fetch(skipUrl, {
                                            method: "POST",
                                            headers: { "content-type": "application/json" },
                                            body: JSON.stringify(userList)
                                        });
                                        const skipData = await skipRes.json();
                                        if (skipData.message?.toLowerCase() === "success") {
                                            openSnackbar(`Sua licença ${licenseType?.type} foi ativada com sucesso`, "success");
                                            setIsNeedRestart(true);
                                            needRestart = true;
                                            setKey("");
                                        } else {
                                            openSnackbar(skipData.error || "Erro ao ativar licença.", "error");
                                        }
                                    } catch (e) {
                                        openSnackbar("Erro ao ativar licença.", "error");
                                    } finally {
                                        await doRestart();
                                    }
                                }
                            });
                        },
                        limiterror: () => openSnackbar("Limite de usuários atingido", "error"),
                        used: () => openSnackbar("Esta chave já está em uso. Adquira uma nova chave.", "error"),
                        error: () => openSnackbar("Erro ao ativar licença. Verifique se a chave foi inserida corretamente", "error"),
                        notfound: () => openSnackbar("Chave não encontrada.", "error"),
                        invalid: () => openSnackbar("Chave inválida.", "error"),
                        expirationerror: () => openSnackbar("Chave expirada. Entre em contato com o suporte.", "error"),
                        errorbypass: () => openSnackbar("Erro ao ativar licença. Você precisa ativar uma licença PRO LOCAL", "error"),
                        timeouterror: () => openSnackbar("Tempo esgotado. Verifique sua conexão e tente novamente.", "warning"),
                        networkerror: () => openSnackbar("Erro de rede. Verifique sua internet.", "warning"),
                        serverunavailable: () => openSnackbar("Servidor indisponível. Tente novamente em alguns minutos.", "warning")
                    };

                    const handler = messageHandlers[resData.message.toLowerCase()] || messageHandlers.error;

                    if (handler) {
                        handler();
                    } else if (!!resData.error) {
                        openSnackbar(resData.error, "error");
                    } else {
                        openSnackbar(`Sua licença ${licenseType?.type} foi ativada com sucesso`, "success");
                        setIsNeedRestart(true);
                        needRestart = true;                                                
                    }
                }
                catch (error) 
                {
                    if (error.message === "cancel" || error.message === "email-exists") {
                        // Não faz nada, usuário já foi notificado
                    } else {
                        openSnackbar(error.message || "Erro ao ativar licença", "error");
                    }
                }
                finally 
                {
                    // Se há um dialog de conflito aguardando resposta do usuário,
                    // não reseta o loading — será resetado pelo onConfirm/onCancel do dialog
                    if (!isConflictPending) {
                        await doRestart();
                    }
                }
            }
        });
    };

    const handleRenew = () => {
        setIsLoading(true);
        setTimeout(() => setIsLoading(false), 1000); // Simula ação
    };

    const VerifyWebMasterUser = () => {
        return users.some(user => user.booL_MASTER === true);
    }

    const getFallbackUserToWebMaster = () => {
        return new Promise((resolve, reject) => {
            if (users.length > 0) {
                let updatedUsers = [...users];
                // Se não tem e-mail, solicita via PromptDialog
                if (!updatedUsers[0].email || updatedUsers[0].email.trim() === "") {
                    openPromptDialog({
                        title: "E-mail obrigatório",
                        message: "Por favor, insira um e-mail válido para permitir o acesso à plataforma WEB.",
                        placeholder: "E-mail",
                        validate: Helper.IsValidEmail,
                        pattern: "^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$",
                        onConfirm: (email) => {
                            /* Verificar se o e-mail está disponível, caso contrário, mensagem de erro que será resolvido pelo WebLicks */

                            if (!verifyEmailExists(email.trim())) {
                                openConfirmationDialog({
                                    title: "E-mail em uso",
                                    message: "O e-mail informado já está em uso. Por favor, entre em contato com o Suporte da Delta Fire.",
                                    confirmButtonText: "OK",
                                    onConfirm: () => {
                                        setIsLoading(false);
                                        reject("cancel");
                                    }
                                });

                                return;
                            }

                            updatedUsers[0].booL_MASTER = true;
                            updatedUsers[0].email = email.trim();
                            setUsers(updatedUsers);
                            openSnackbar(`O usuário ${updatedUsers[0].dS_NOME} será definido como Web Master automaticamente após a ativação da licença.`, "info");
                            resolve(updatedUsers);
                        },
                        onCancel: () => {
                            openConfirmationDialog({
                                title: "Atenção",
                                message: "Sem um e-mail válido, o acesso à plataforma WEB do Guardião 2.0 não será possível.\n\nDeseja encerrar o processo de ativação da licença?",
                                confirmButtonText: "Encerrar",
                                cancelButtonText: "Voltar",
                                onConfirm: () => {
                                    setIsLoading(false);
                                    reject("cancel");
                                },
                                onCancel: () => {
                                    // Tenta novamente
                                    getFallbackUserToWebMaster().then(resolve).catch(reject);
                                }
                            });
                        }
                    });
                } else {
                    updatedUsers[0].booL_MASTER = true;
                    setUsers(updatedUsers);
                    openSnackbar(`O usuário ${updatedUsers[0].dS_NOME} será definido como Web Master automaticamente após a ativação da licença.`, "info");
                    resolve(updatedUsers);
                }
            } else {
                reject("no-users");
            }
        });
    }

    const ensureWebMasterUser = async () => {
        if (users.length === 0) throw new Error("Nenhum usuário disponível.");

        let updatedUsers = [...users];
        let email = updatedUsers[0].email?.trim();

        while (!email) {
            try {
                email = await promptForWebMasterEmail();
            } catch (err) {
                // Cancelado pelo usuário
                const shouldAbort = await new Promise((resolve) => {
                    openConfirmationDialog({
                        title: "Atenção",
                        message: "Sem um e-mail válido, o acesso à plataforma WEB do Guardião 2.0 não será possível.\n\nDeseja encerrar o processo de ativação da licença?",
                        confirmButtonText: "Encerrar",
                        cancelButtonText: "Voltar",
                        onConfirm: () => resolve(true),
                        onCancel: () => resolve(false)
                    });
                });
                if (shouldAbort) throw new Error("cancel");
                // Se não abortou, volta ao loop para pedir o e-mail novamente
            }
        }

        // Verifica se o e-mail já existe
        setIsLoading(true);
        const exists = await verifyEmailExists(email);
        setIsLoading(false);

        if (exists) {
            await new Promise((resolve) => {
                openConfirmationDialog({
                    title: "E-mail em uso",
                    message: "O e-mail informado já está em uso. Por favor, entre em contato com o Suporte da Delta Fire.",
                    confirmButtonText: "OK",
                    onConfirm: resolve
                });
            });
            throw new Error("email-exists");
        }

        updatedUsers[0].booL_MASTER = true;
        updatedUsers[0].email = email;
        setUsers(updatedUsers);
        openSnackbar(`O usuário ${updatedUsers[0].dS_NOME} será definido como Web Master automaticamente após a ativação da licença.`, "info");
        return updatedUsers;
    };

    const promptForWebMasterEmail = () => {
        return new Promise((resolve, reject) => {
            openPromptDialog({
                title: "E-mail obrigatório",
                message: "Por favor, insira um e-mail válido para permitir o acesso à plataforma WEB.",
                placeholder: "E-mail",
                validate: Helper.IsValidEmail,
                pattern: "^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$",
                onConfirm: resolve,
                onCancel: () => reject("cancel")
            });
        });
    };

    const verifyEmailExists = async (email) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_URL}/licences/user/exists?email=${email}`, {
                method: 'GET',
                headers: {
                    "content-type": "application/json",
                    "Authorization": "Bearer " + props.token
                }
            });
            if (!response.ok) throw new Error("Erro ao verificar e-mail");
            const exists = await response.json();
            return !!exists; // true se existe, false se não
        }
        catch (error) {
            openSnackbar("Erro ao verificar e-mail.", "error");
            return true; // Por segurança, bloqueia o fluxo
        }
    }

    const getLicensesInfoOnline = async () => {
        try {
            setIsLoading(true);
            const onlineRequest = await fetch(`${process.env.REACT_APP_URL}/user/all?companyId=${session.companyId}`, {
                method: 'GET',
                headers: {
                    "content-type": "application/json",
                    "Authorization": "Bearer " + props.token
                }
            });

            if (!onlineRequest.ok) {
                throw new Error("Erro ao buscar informações de licenças.");
            }
            else {
                const usersList = await onlineRequest.json();
                // Parse para o model User
                const parsedUsers = usersList.map(u => User.fromApi(u));
                setUsers(parsedUsers);
            }
        }
        catch (error) {
            console.error("Erro ao buscar informações de licenças:", error);
            openSnackbar("Erro ao buscar informações de licenças.", "error");
        }
        finally {
            setIsLoading(false);
        }
    }

    const getLicensesInfoOffine = async () => {
        try {
            const offlineRequest = await fetch(`${process.env.REACT_APP_URL}/offlinelicences/get?companyId=${session.companyId}`, {
                method: 'GET',
                headers: {
                    "content-type": "application/json",
                    "Authorization": "Bearer " + props.token
                }
            });
            if (!offlineRequest.ok) {
                throw new Error("Erro ao buscar informações de licenças offline.");
            }
            const offlineData = await offlineRequest.json();
            

            setUsers([{ ...commandsProvider.user, iD_INTRANET: 0 }])
            //setOfflineLicence(offlineData);
        }
        catch (error) {
            console.error("Erro ao buscar informações de licenças offline:", error);
            openSnackbar("Erro ao buscar informações de licenças offline.", "error");
        }
        finally {
            //setIsOfflineInfo(false);
            setIsLoading(false);
        }
    }

    useEffect(() => {
        /**Ver a necessidade de buscar essas informações de usuários */
        // Teste de conexão e busca de licenças
        (async () => {
            const online = await Helper.TestIfIsOnline();
            if (online) {
                await getLicensesInfoOnline();
            } else {
                await getLicensesInfoOffine();
            }
        })();
    }, []);

    let helperText = "";
    if (key.length === 0) {
        helperText = "Digite sua chave de licença";
    }
    else if (licenseType && isValid !== false) {
        helperText = `Licença ${licenseType.label}`;
    }
    else if (!licenseType || (key.length >= 1 && key.length < 32)) {
        helperText = "Tipo de licença não reconhecido";
    }
    else if (isValid === false) {
        helperText = `Chave inválida ou não pertence ao sistema da central ${session.serial}`;
    }

    return (
        <Card elevation={4} className={classes.card}>
            {isLoading && (
                <LinearProgress
                    color="secondary"
                    sx={{
                        width: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        borderTopLeftRadius: 1,
                        borderTopRightRadius: 1
                    }}
                />
            )}
            <CardHeader
                avatar={<Key />}
                title="Ative sua licença"
            />
            <CardContent>
                <FormControl fullWidth error={isValid === false}>
                    <OutlinedInput
                        id="secret-key"
                        className={classes.key}
                        variant="outlined"
                        color="secondary"
                        fullWidth
                        value={key}
                        required
                        disabled={isLoading}
                        onChange={handleKeyChange}
                        startAdornment={
                            <InputAdornment position="start">
                                <Key />
                            </InputAdornment>
                        }
                    />
                    <FormHelperText sx={{ marginLeft: "2px" }}>
                        {helperText}
                    </FormHelperText>
                </FormControl>
            </CardContent>
            <CardActions>
                <Grid container spacing={2}>
                    <Grid item gap={16} xs={12} sx={{ display: "flex", justifyContent: "end", paddingTop: 0 }}>
                        <Button
                            disabled={isLoading || !isValid || key.length < 30}
                            variant="contained"
                            color="secondary"
                            onClick={handleActivate}
                        >
                            {`Ativar Licença ${licenseType ? licenseType.label : ""}`}
                        </Button>
                    </Grid>
                </Grid>
            </CardActions>
        </Card>
    );
}